import React, {
  Fragment,
} from 'react';
import clsx from 'clsx';
import {
  formatTemplate,
  NAVIGATION_PAGE_IDS,
  walkthroughContent,
} from 'views/EMR/components/PCC/Walkthrough/content';
import WalkthroughIcon from 'views/EMR/components/PCC/Walkthrough/icons';
import PCCIcon from 'core/icons/pcc';
import type {
  NavigationPageId,
} from 'views/EMR/components/PCC/Walkthrough/types';

type Props = {
  activePageId: NavigationPageId;
  onNavigate: (pageId: NavigationPageId) => void;
  onClose: () => void;
  disabled?: boolean;
  isConfigureUnlocked?: boolean;
};

export default function WalkthroughHeader({
  activePageId,
  onNavigate,
  onClose,
  disabled = false,
  isConfigureUnlocked = false,
}: Props) {
  const activeIndex = NAVIGATION_PAGE_IDS.indexOf(activePageId);
  const activePage = walkthroughContent.pages.find(
    (page) => page.id === activePageId,
  );
  const mobileProgress = formatTemplate(
    walkthroughContent.shared.mobileProgressTemplate,
    {
      current: activeIndex + 1,
      total: NAVIGATION_PAGE_IDS.length,
    },
  );

  return (
    <div className="pcc-walkthrough-header flex shrink-0 flex-col border-b border-[#E2E8F0] bg-white">
      <div className="flex items-center justify-between gap-3 px-4 py-3 sm:px-5 lg:min-h-[72px] lg:px-8 lg:py-0">
        <div className="flex items-center gap-2.5">
          <div className="h-7 w-7 rounded-lg bg-brand-500 flex items-center justify-center" aria-hidden="true">
            <PCCIcon className="!h-6 !w-6 !text-white" />
          </div>
          <div className="text-[15px] font-bold text-[#0F1B2D]">
            {walkthroughContent.shared.headerTitle}
          </div>
        </div>

        <nav
          aria-label={walkthroughContent.shared.stepsNavAriaLabel}
          className="pcc-walkthrough-stepper hidden items-start md:flex"
        >
          {NAVIGATION_PAGE_IDS.map((pageId, index) => {
            const page = walkthroughContent.pages.find(
              (candidate) => candidate.id === pageId,
            );
            const isActive = pageId === activePageId;
            const isComplete = index < activeIndex;
            const isUpcoming = index > activeIndex;
            const isConfigureLocked = pageId === 'configure' && !isConfigureUnlocked;
            const isStepDisabled = disabled || isConfigureLocked;

            return (
              <Fragment key={pageId}>
                {index > 0 ? (
                  <div
                    aria-hidden="true"
                    className={clsx(
                      'mt-[13px] h-0.5 w-[18px]',
                      index <= activeIndex ? 'bg-brand-500' : 'bg-slate-300',
                    )}
                  />
                ) : null}
                <button
                  type="button"
                  aria-current={isActive ? 'step' : undefined}
                  disabled={isStepDisabled}
                  onClick={() => onNavigate(pageId)}
                  className={clsx(
                    'flex w-[84px] flex-col items-center gap-1.5',
                    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500',
                    isStepDisabled && 'cursor-not-allowed opacity-60',
                  )}
                >
                  <div
                    className={clsx(
                      'box-border flex h-7 w-7 items-center justify-center rounded-full text-[13px]',
                      {
                        'bg-brand-500 text-white': isComplete,
                        'border-2 border-brand-500 bg-white font-bold text-brand-700': isActive,
                        'border border-slate-300 bg-white font-semibold text-slate-500': isUpcoming,
                      },
                    )}
                  >
                    {isComplete ? (
                      <WalkthroughIcon
                        iconKey="check"
                        className="!h-3.5 !w-3.5 !text-white"
                      />
                    ) : (
                      index + 1
                    )}
                  </div>
                  <span
                    className={clsx(
                      'whitespace-nowrap text-[11px]',
                      {
                        'font-bold text-brand-700': isActive,
                        'font-medium text-[#475569]': isComplete,
                        'font-medium text-slate-500': isUpcoming,
                      },
                    )}
                  >
                    {page?.stepLabel}
                  </span>
                </button>
              </Fragment>
            );
          })}
        </nav>

        <button
          type="button"
          aria-label={walkthroughContent.shared.closeAriaLabel}
          disabled={disabled}
          onClick={onClose}
          className={clsx(
            'flex h-11 w-11 items-center justify-center rounded-full',
            'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500',
            disabled ? 'cursor-not-allowed opacity-50' : 'hover:bg-slate-100',
          )}
        >
          <WalkthroughIcon
            iconKey="close"
            className="!h-5 !w-5 !text-[#475569]"
          />
        </button>
      </div>

      <div className="border-t border-[#E2E8F0] px-4 py-2.5 md:hidden">
        <div className="text-xs font-bold uppercase tracking-[0.08em] text-brand-700">
          {mobileProgress}
        </div>
        <div className="truncate text-sm font-semibold text-[#0F1B2D]">
          {activePage?.stepLabel}
        </div>
      </div>
    </div>
  );
}
